import { components as c } from "@/components";
import { formComponents as fc } from "@/features/i18n/formComponents";
import ShockwaveFormFields from "@/features/shockwave/components/ShockwaveFormFields";
import ShockwaveDetailsFormFields from "@/features/shockwave/components/ShockwaveDetailsFormFields";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { SubmitHandler, useForm } from "react-hook-form";
import { diaryByIdQueryOptions } from "@/features/diaries";
import { useShockwaveMutation } from "@/features/shockwave/api/upsertShockwave";
import { useReplaceShockwaveDetails } from "@/features/shockwave/api/useReplaceShockwaveDetails";
import {
    formatShockwaveDetailsDefaultValues,
    ShockwaveDetailSchemaT,
    ShockwaveDetailsZ,
} from "@/features/shockwave/components/ShockwaveDetailsForm";
import { extractEventById } from "@/features/diaries/services/extractEventById";
import { FormErrors } from "@patient-watch/forms";
import { ui } from "@patient-watch/ui";
import { useAuth } from "@/utils/auth";
import { z } from "zod";
import { useState, useEffect } from "react";
import { insertEvent } from "@/features/events/api/insertEvent";
import { updateDiaryFlowSearchSchema } from "@/features/patients/update/updateDiaryFlowSearch";

const searchSchema = updateDiaryFlowSearchSchema.extend({
    event_type: z.literal("shockwave"),
});

const shockwaveSchemaZ = z.object({
    event_id: z.string().uuid(),
    shockwave_id: z.string().uuid(),
    referring_clinician_id: z.string().uuid().optional(),
    shockwaving_clinician_id: z.string().uuid().optional(),
});

type ShockwaveSchemaT = z.infer<typeof shockwaveSchemaZ>;
type Stage = "clinicians" | "details";

const AddShockwavePage = () => {
    const { diaryId, eventId, trackerId } = Route.useParams();
    const search = Route.useSearch();
    const { type } = search;
    const { diary, shockwave } = Route.useLoaderData();
    const auth = useAuth();
    const navigate = useNavigate();
    const [stage, setStage] = useState<Stage>(() => (shockwave ? "details" : "clinicians"));

    // Generate shockwave ID
    const shockwaveId = shockwave?.shockwave_id ?? crypto.randomUUID();

    const shockwaveMutation = useShockwaveMutation({ diaryId });
    const replaceShockwaveDetails = useReplaceShockwaveDetails({ diaryId });

    // Clinicians form
    const cliniciansForm = useForm<ShockwaveSchemaT>({
        resolver: zodResolver(shockwaveSchemaZ),
        defaultValues: {
            event_id: eventId,
            shockwave_id: shockwaveId,
            referring_clinician_id: shockwave?.referring_clinician_id ?? search.referring_clinician_id ?? auth.id,
            shockwaving_clinician_id: shockwave?.shockwaving_clinician_id ?? search.shockwaving_clinician_id ?? auth.id,
        },
    });

    // Details form
    const detailsForm = useForm<ShockwaveDetailSchemaT>({
        resolver: zodResolver(ShockwaveDetailsZ),
        defaultValues: formatShockwaveDetailsDefaultValues(shockwaveId, shockwave),
    });

    useEffect(() => {
        if (shockwave) {
            detailsForm.reset(formatShockwaveDetailsDefaultValues(shockwaveId, shockwave));
        }
    }, [shockwave, shockwaveId, detailsForm]);

    const handleCliniciansSubmit: SubmitHandler<ShockwaveSchemaT> = async (formData) => {
        // First, ensure the event exists
        await insertEvent({ event_id: eventId, diary_id: diaryId });
        // Then insert the shockwave
        shockwaveMutation.mutate(formData, {
            onSuccess: () => setStage("details"),
        });
    };

    const handleDetailsSubmit: SubmitHandler<ShockwaveDetailSchemaT> = async (formData) => {
        replaceShockwaveDetails.mutate(
            {
                ...formData,
                shockwave_id: shockwaveId,
            },
            {
                onSuccess: () => {
                    if (search.from === "worklist") {
                        void navigate({ to: "/worklist" });
                        return;
                    }
                    void navigate({
                        to: "/patients/$diaryId/update/$eventId/$trackerId/date",
                        params: { diaryId, eventId, trackerId },
                        search: { ...search, type },
                    });
                },
            }
        );
    };

    if (stage === "clinicians") {
        return (
            <ui.Form {...cliniciansForm}>
                <form onSubmit={cliniciansForm.handleSubmit(handleCliniciansSubmit)} className="space-y-6">
                    <h2 className="font-semibold text-lg">Shockwave Clinicians</h2>
                    <ShockwaveFormFields organisationId={diary?.organisation_id as string} />
                    <fc.ContinueButton buttonType="submit" size="lg" className="w-full" />
                </form>
            </ui.Form>
        );
    }

    return (
        <c.ui.Form {...detailsForm}>
            <form onSubmit={detailsForm.handleSubmit(handleDetailsSubmit)} className="space-y-6">
                <h2 className="font-semibold text-lg">Shockwave Details</h2>
                <ShockwaveDetailsFormFields shockwaveId={shockwaveId} />
                <FormErrors />
                <fc.NextButton buttonType="submit" size="lg" className="w-full" />
            </form>
        </c.ui.Form>
    );
};

export const Route = createFileRoute(
    "/_app/patients/$diaryId/update/$eventId/$trackerId/intervention-details/shockwave"
)({
    component: AddShockwavePage,
    validateSearch: searchSchema,
    loader: async ({ context, params }) => {
        const diary = await context.queryClient.ensureQueryData(diaryByIdQueryOptions(params.diaryId));
        const event = extractEventById(diary!, params.eventId);
        const shockwave = event?.shockwaves_view?.[0] ?? undefined;
        return { diary, event, shockwave };
    },
});
